import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import { Separator } from '@workspace/ui/components/separator';
import { cn } from '@workspace/ui/lib/utils';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Layers,
  Shield,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { m } from '~/paraglide/messages.js';
import { getLocale } from '~/paraglide/runtime.js';
import type { CardMetadata } from '../../-types/card';
import {
  getMccLabel,
  MCC_CATEGORIES,
} from '../../create/-constants/mcc-categories';

interface SpendingControlsProps {
  metadata: CardMetadata;
}

export function SpendingControls({ metadata }: SpendingControlsProps) {
  const mode = metadata.spending_control ?? 'default';

  return (
    <Card className="border-purple-500/30 bg-transparent shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-purple-400" />
            {m['cards.spending.title']()}
          </CardTitle>
          <Badge variant={mode === 'smart' ? 'default' : 'secondary'}>
            {mode === 'smart'
              ? m['cards.spending.mode_smart']()
              : m['cards.spending.mode_default']()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {mode === 'smart' ? (
          <SmartModeView metadata={metadata} />
        ) : (
          <DefaultModeView metadata={metadata} />
        )}
      </CardContent>
    </Card>
  );
}

function DefaultModeView({ metadata }: { metadata: CardMetadata }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {m['cards.spending.default_description']()}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {metadata.default_asset && (
          <InfoItem
            label={m['cards.spending.default_asset']()}
            value={metadata.default_asset}
          />
        )}
        {metadata.fallback_asset && (
          <InfoItem
            label={m['cards.spending.fallback_asset']()}
            value={metadata.fallback_asset}
          />
        )}
      </div>

      {metadata.currency_asset_map &&
        Object.keys(metadata.currency_asset_map).length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">
              {m['cards.spending.currency_map']()}
            </p>
            <div className="space-y-1">
              {Object.entries(metadata.currency_asset_map).map(
                ([currency, assets]) => (
                  <div
                    key={currency}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Badge variant="outline" className="text-xs">
                      {currency}
                    </Badge>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {Array.isArray(assets)
                        ? assets.join(', ')
                        : String(assets)}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

      {metadata.template && (
        <div className="flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {m['cards.spending.template']()}:{' '}
          </span>
          <Badge variant="secondary" className="capitalize">
            {metadata.template}
          </Badge>
        </div>
      )}
    </div>
  );
}

function SmartModeView({ metadata }: { metadata: CardMetadata }) {
  const locale = getLocale() as 'en' | 'es';
  const priorityMcc = metadata.priority_mcc ?? [];
  const mccWhitelist = metadata.mcc_whitelist ?? {};

  const pockets = useMemo(() => {
    return priorityMcc.map((pocketUrn, index) => {
      const mccCodes = mccWhitelist[pocketUrn];
      const codes = Array.isArray(mccCodes)
        ? mccCodes
        : mccCodes
          ? [mccCodes]
          : [];

      const isCatchAll = index === priorityMcc.length - 1 && codes.length === 0;

      const categoryMatch = MCC_CATEGORIES.find((cat) => {
        const catCodes = cat.subcategories.map((s) => s.code);
        return codes.length > 0 && codes.every((c) => catCodes.includes(c));
      });

      return {
        urn: pocketUrn,
        codes,
        isCatchAll,
        category: categoryMatch,
        label: categoryMatch
          ? categoryMatch.id
          : isCatchAll
            ? 'general'
            : (pocketUrn.split(':').pop() ?? pocketUrn),
      };
    });
  }, [priorityMcc, mccWhitelist, locale]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {m['cards.spending.smart_description']()}
      </p>

      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
        <Zap className="h-3 w-3" />
        {m['cards.spending.priority_chain']()}
      </div>

      <div className="flex items-start gap-2 overflow-x-auto pb-2">
        {pockets.map((pocket, index) => (
          <div key={pocket.urn} className="flex items-start gap-2">
            <PocketCard pocket={pocket} locale={locale} />
            {index < pockets.length - 1 && (
              <ArrowRight className="h-4 w-4 text-muted-foreground mt-8 shrink-0" />
            )}
          </div>
        ))}
      </div>

      {metadata.template && (
        <div className="flex items-center gap-2 pt-2">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {m['cards.spending.template']()}:{' '}
          </span>
          <Badge variant="secondary" className="capitalize">
            {metadata.template}
          </Badge>
        </div>
      )}
    </div>
  );
}

function PocketCard({
  pocket,
  locale,
}: {
  pocket: {
    urn: string;
    codes: string[];
    isCatchAll: boolean;
    category: (typeof MCC_CATEGORIES)[number] | undefined;
    label: string;
  };
  locale: 'en' | 'es';
}) {
  const [expanded, setExpanded] = useState(false);

  const Icon = pocket.category?.icon;

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border border-purple-500/20 p-3 min-w-[160px] max-w-[200px] shrink-0 transition-all',
        pocket.isCatchAll && 'border-dashed border-purple-500/15',
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium capitalize">{pocket.label}</span>
      </div>

      {pocket.isCatchAll ? (
        <p className="text-xs text-muted-foreground">
          {m['cards.spending.pocket_catch_all']()}
        </p>
      ) : (
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
            >
              {pocket.codes.length} MCCs
              {expanded ? (
                <ChevronUp className="ml-1 h-3 w-3" />
              ) : (
                <ChevronDown className="ml-1 h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Separator className="my-2" />
            <ul className="space-y-1">
              {pocket.codes.map((code) => (
                <li key={code} className="text-xs text-muted-foreground">
                  <span className="font-mono">{code}</span>{' '}
                  {getMccLabel(code, locale)}
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-purple-500/20 p-3">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-medium font-mono">{value}</p>
    </div>
  );
}
